import { push, pushOrReplace } from '@/common/utils';
import { LOCAL_STORAGE_SENDBOX_SENT } from '@/common/constants';
import localStorageUtil from './localStorageUtil';

const DEBUG = false;

function getSendboxSentItems(state) {
  let sendBoxLocalStorageItem = localStorage.getItem(LOCAL_STORAGE_SENDBOX_SENT);
  let result = [];
  try {
    result = JSON.parse(sendBoxLocalStorageItem ? sendBoxLocalStorageItem : '[]');
  } catch (err) {
    result = [];
  }

  // remove any items that were successfully synced with database
  result = result.filter((item) => item.status !== 200);
  localStorage.setItem(LOCAL_STORAGE_SENDBOX_SENT, JSON.stringify(result));

  return result;
}

function getValueFromTwoLevelObject(obj, key1, key2) {
  let subObj = obj[key1];
  if (subObj) {
    return subObj[key2];
  }
  return null;
}

function buildIdMap(items, idAttribute = 'id') {
  const map = {};
  if (items) {
    items.forEach((item) => {
      map[item[idAttribute]] = item;
    });
  }
  return map;
}

/**
 * Count items and aggregate throughout the tree until we reach the root.
 * The attribute parameter is the name of the attribute to count and aggregate.
 * @param documents array of documents
 * @param instanceFolderTrees an object whose keys are the INSTANCE (of a module) and the values are
 *                    arrays of folders and items (e.g. documents)
 * @param foldersById Object of folders by their id. Each folder requires a parentId attribute
 *                    referencing the parent folder. a parentId=0 means their is no parent folder.
 * @param attribute   The attribute on folder items holding the counter to sum up
 */
function countSpecialTreeItems({ documents, instanceFolderTrees, foldersById, attribute = '_unreadCount', calculateCountFunc }) {
  // reset counters
  Object.keys(foldersById).forEach((id) => (foldersById[id][attribute] = 0));
  Object.keys(instanceFolderTrees).forEach((instanceNum) => (instanceFolderTrees[instanceNum][attribute] = 0));

  // calculate counters for each document by walking hierarchy up until module instance root
  documents.forEach((doc) => {
    let count = calculateCountFunc(doc);

    sumUpHierarchy({
      instanceFolderTrees,
      foldersById,
      docInstance: doc.instance,
      folderId: doc.folderIdFk,
      attribute,
      value: count,
      doc,
    });
  });
}

/**
 * Sum up the value up the tree
 * @param {*} param0
 */
export function sumUpHierarchy({ instanceFolderTrees, foldersById, docInstance, folderId, attribute, value, doc }) {
  // if (!total) total = 0;
  if (folderId === 0) {
    // that is "the root"

    instanceFolderTrees[docInstance][attribute] += value;

    // if (docInstance == '1' && attribute === '_unreadCount') {
    //     console.log("root file ", doc ? doc.title : 'no doc, value', value, 'new total', instanceFolderTrees[docInstance][attribute])
    // }
  } else {
    // store total in the desired attribute
    if (foldersById[folderId]) {
      foldersById[folderId][attribute] += value;
      // and step up within the hierarchy
      sumUpHierarchy({
        instanceFolderTrees,
        foldersById,
        docInstance,
        folderId: foldersById[folderId].parentId,
        attribute,
        value,
        doc,
      });
    }
  }
}

/**
 * Adds the document to the appropriate location within the folder tree
 * @param document
 * @param instanceFolderTrees
 * @param foldersById
 */
function addToInstanceFolderTrees(document, instanceFolderTrees, foldersById) {
  if (document.folderIdFk === 0) {
    if (!instanceFolderTrees[document.instance]) {
      instanceFolderTrees[document.instance] = { root: [] };
    }
    instanceFolderTrees[document.instance].root.push(document);
  } else {
    let folder = foldersById[document.folderIdFk];
    if (folder) {
      pushOrReplace(folder, 'children', 'id', document);
    }
  }
}

function lastItem(array) {
  if (array.length > 0) return array[array.length - 1];
  return null;
}

export default {
  /**
   * Searches and returns all items of searchItems found in investigationArray
   * @param investigationArray - array to investigate
   * @param searchItems - array with items to look up
   */
  findContainedItems(investigationArray, searchItems) {
    return searchItems.filter((i) => investigationArray.includes(i));
  },
  /**
   * Apply user states stored in localStorage if the userState is newer than what is stored on the item.
   * Delete from local storage if we have a newer state on the object
   * Do not delete otherwise as it is obvious now, the state has not been stored on backend.
   * @param {*} itemsById
   * @param {*} userStateType
   * @param {*} userStateItemIdAttribute
   */
  applyLocalStorageUserState(itemsById, userStateType, userStateItemIdAttribute) {
    let userStates = localStorageUtil.getUserStates();
    const sendboxSentItems = getSendboxSentItems();

    let thisUserStates = userStates[userStateType] || [];
    let userStatesToKeepInLocalStorage = [];

    const readStatusClearableModules = ['documents', 'fahrdienst', 'fahrdienst452'];

    const docExistInLocalstorage = (item) => {
      if (readStatusClearableModules.includes(item._module)) {
        return sendboxSentItems.find((localStorageItem) => localStorageItem.subject.id === item.id && localStorageItem.subject._module === item._module);
      }
      return false;
    };

    thisUserStates.forEach((localStorageUserState) => {
      let item = itemsById[localStorageUserState[userStateItemIdAttribute]];
      if (item) {
        let userStateOnItem = item._userState;

        let isLocalStorageItemNewer = docExistInLocalstorage(item) ? this.isUserState2NewerThanUserState1(userStateOnItem, localStorageUserState) : false;
        let isUnsentInSendBox = this.isUnsentInSendBox(item._moduleId, userStateOnItem[userStateItemIdAttribute]);

        if (isUnsentInSendBox || isLocalStorageItemNewer) {
          item._userState = Object.assign({}, localStorageUserState);

          if (item._moduleId && item._moduleId == 'documents') {
            if (item.referenceId) {
              if (itemsById[item.referenceId]) {
                itemsById[item.referenceId]._userState = Object.assign({}, localStorageUserState);
              }
            } else {
              for (const [id, value] of Object.entries(itemsById)) {
                if (value?.referenceId === item.id) {
                  if (itemsById[id]) {
                    itemsById[id]._userState = Object.assign({}, localStorageUserState);
                  }
                }
              }
            }
          }

          userStatesToKeepInLocalStorage.push(localStorageUserState);
        } else {
          console.log('local storage object is older and is omitted in localStorage though');
        }
      } else {
        console.log(`Failed to find item for userState ${userStateType} ${JSON.stringify(localStorageUserState, null, 2)}`);
      }
    });
    // console.log(
    //   'Store remaining (that is not yet backend stored) userStates to the localStorage',
    //   userStateType,
    //   JSON.stringify(userStatesToKeepInLocalStorage, null, 2)
    // );
    localStorageUtil.setUserStates(userStateType, userStatesToKeepInLocalStorage);
  },

  /**
   * check the local storage sendbox to see if there is an unsent entity with the same moduleId and entityId
   * @param {*} moduleId
   * @param {*} entityId
   * @returns
   */
  isUnsentInSendBox(moduleId, entityId) {
    let foundInLocalStorage = false;
    let localStorageSendBox = localStorage.getItem('sendbox');
    if (localStorageSendBox) {
      try {
        let localStorageSendBoxArray = JSON.parse(localStorageSendBox);
        if (Array.isArray(localStorageSendBoxArray)) {
          for (let localStorageSendBoxItem of localStorageSendBoxArray) {
            if (localStorageSendBoxItem?.moduleId === moduleId && localStorageSendBoxItem?.subject?.id === entityId) {
              foundInLocalStorage = true;
              break;
            }
          }
        }
      } catch (err) {
        // error while parsing sendbox, ignore
      }
    }
    return foundInLocalStorage;
  },

  /**
   * Returns true if the userdatemodified field of the userState2 object is greater, that is newer than the userdatemodified of userState1
   * @param {*} userState1
   * @param {*} userState2
   * @returns true if the userState2 is newer than userState1
   */
  isUserState2NewerThanUserState1(userState1 = {}, userState2 = {}) {
    let timeUserState1 = Date.parse(userState1.userdatemodified);
    let timeUserState2 = Date.parse(userState2.userdatemodified);

    return timeUserState2 > timeUserState1;
  },
  /**
   *
   * @param {*} param0
   * @returns
   */
  getLatestUserState({ item, loadedUserState, userStatePrototype }) {
    let existingUserState = item._userState;
    let userState;

    if (existingUserState || loadedUserState) {
      // let comparison = this.isUserState2NewerThanUserState1(existingUserState, loadedUserState);
      // console.log('getLatestUserState', 'onItem', JSON.stringify(existingUserState));
      // console.log('getLatestUserState', 'loadedUserState', JSON.stringify(loadedUserState));
      // console.log('getLatestUserState', comparison ? 'loadedUserState' : 'item userState', 'is newer');

      userState = this.isUserState2NewerThanUserState1(existingUserState, loadedUserState) ? loadedUserState : existingUserState;
    }
    if (!userState) {
      userState = userStatePrototype;
    }
    return userState;
  },
  lastItem,
  addToInstanceFolderTrees,
  countSpecialTreeItems,
  getValueFromTwoLevelObject,
  /**
   * Pushes the item to the array named [attributeName]. If the array is undefined, it'll be created.
   * @param {Object} object The object to push the item
   * @param {String} attributeName the name of the array attribute
   * @param {Object} item the item to push
   */
  push(object, attributeName, item) {
    if (object) {
      if (!object[attributeName]) {
        object[attributeName] = [];
      }
      object[attributeName].push(item);
    }
  },
  buildIdMap,
  buildTwoLevelObjectWithArrayLeaves(items, keyBuilderFirstLevel, keyBuilderSecondLevel) {
    const map = {};
    if (items) {
      items.forEach((item) => {
        let firstLevelKey = keyBuilderFirstLevel(item);
        let secondLevelKey = keyBuilderSecondLevel(item);

        let subObject = map[firstLevelKey];
        if (!subObject) {
          subObject = {};
          map[firstLevelKey] = subObject;
        }

        let array = subObject[secondLevelKey];
        if (!array) {
          array = [];
          subObject[secondLevelKey] = array;
        }
        array.push(item);
      });
    }
    return map;
  },
  buildFoldersByIdAndTree(folders) {
    let foldersById = {};
    folders.forEach((folder) => {
      folder.type = 'folder';
      foldersById[folder.id] = folder;
    });

    // add to parent folders children array
    folders.forEach((folder) => {
      let parentId = folder.parentId;
      if (parentId !== 0) {
        let parentFolder = foldersById[parentId];
        if (parentFolder) {
          pushOrReplace(parentFolder, 'children', 'id', folder);
        } else {
          DEBUG && console.log(`No parent folder with id ${parentId}`);
        }
      }
    });
    return foldersById;
  },

  /**
   * Generate hierarchy by listing top level folders in instanceFolderTrees (by instance)
   * @param {*} folders
   * @returns the object of instance to folder trees
   */
  buildInstanceFolderTrees(folders) {
    let instanceFolderTrees = {};
    folders.forEach((folder) => {
      if (folder.parentId === 0) {
        if (!instanceFolderTrees[folder.instance]) {
          instanceFolderTrees[folder.instance] = { root: [] };
        }
        instanceFolderTrees[folder.instance].root.push(folder);
      }
    });
    return instanceFolderTrees;
  },
  //(tags,documentsById,'tags','documentIdFk')
  /**
   * Associates the elements in [itemsToAttach] to the base items in [baseItemsById] namely by adding them to an
   * array named [associateAttribute]
   * The itemsToAtach need an attribute whose name is provided as associateAttribute parameter. If a baseItem with
   * the id in associateAttribute is found in baseItemsById
   * @param {*} itemsToAssociate Array of items which should be associated to the appropriate baseItem
   * @param {*} baseItemsById Object of items with keys=id
   * @param {*} associateAttribute the attribute name used to store the associated items to
   * @param {*} baseItemReferenceAttribute the attribute name within the itemsToAssociate which references the
   *                                       the baseItems id.
   */
  // dataUtils.associateItemsToBaseList(labelsMapping, documentsById, 'labels', 'fileIdFk');
  associateItemsToBaseList(itemsToAssociate, baseItemsById, associateAttribute, baseItemReferenceAttribute) {
    // Associate tags
    itemsToAssociate.forEach((associateItem) => {
      let baseItem = baseItemsById[associateItem[baseItemReferenceAttribute]];
      if (baseItem) {
        push(baseItem, associateAttribute, associateItem);
      }
    });
  },
  associateItems({ baseItemsById, associateItemsById, mappings, baseItemAttribute, mappingBaseRefAttribute, mappedAssociateRefAttribute }) {
    mappings.forEach((mapping) => {
      let baseItem = baseItemsById[mapping[mappingBaseRefAttribute]];
      let associateItem = associateItemsById[mapping[mappedAssociateRefAttribute]];

      if (baseItem && associateItem) {
        push(baseItem, baseItemAttribute, associateItem);
      }
    });
  },
  /**
   * Attaches a user states object to each file item, namely into the _userState field. If no user state object is present in userStates we create an empty one.
   * @param {Array} files files table
   * @param {Array} userStates user states table
   * @param {String} filesIdAttribute the name of the id attribute in the files table, usually "id"
   * @param {String} userStatesFileReferenceAttribute the name of the foreign key attribute in userStates referencing the files table namely its filesIdAttribute. Example: fileIdFk
   * @param {String} userStateType type of user state information in camel case writing. Be sure ro match mapping in sendboxController with this type!!!.
   *                          Examples documentUserState,fahrdienst452UserState
   */
  attachUserStatesToFiles({ files, userStates, filesIdAttribute, userStatesFileReferenceAttribute, userStateType }) {
    const userStatesByFilesId = buildIdMap(userStates, userStatesFileReferenceAttribute);
    files.forEach((file) => {
      let userState = userStatesByFilesId[file[filesIdAttribute]];
      if (userState) {
        userState._type = userStateType;
      } else {
        // Create a userState object in case it does not exist
        userState = { _type: userStateType };
        userState[userStatesFileReferenceAttribute] = file[filesIdAttribute];
      }
      file._userState = userState;
    });
  },
  /**
   * Builds a map from the given items. However instead of using one given attribute as id, the param idBuilder is a function which calculates the "id"
   * @param {*} items Array
   * @param {*} idBuilder id builder function
   */
  buildCombinedIdMap(items, idBuilder) {
    const map = {};
    if (items) {
      items.forEach((item) => {
        map[idBuilder(item)] = item;
      });
    }
    return map;
  },
  /**
   * Builds a map from the given items. Keys are the calculated id's (idBuilder) and the values are array whose items fullfill the id constraint.
   * @param {array} items Array
   * @param {function} idBuilder id builder function to return a built id
   */
  buildArrayMapById(items, idBuilder) {
    const map = {};
    if (items) {
      items.forEach((item) => {
        const id = idBuilder(item);
        if (!id) return;
        if (!item) return;

        let arr = map[id];
        if (!arr) {
          arr = [];
          map[id] = arr;
        }
        arr.push(item);
      });
    }
    return map;
  },
  /**
   * Builds a map from the given items. However instead of using one given attribute as id, the param idBuilder is a function which calculates the "id".
   * OneToMany means that the list of items might contain multiple items with the same constructed id. Therefore the resulting object is not a flat map, but an object of arrays like so:
   * {
   *        "id1_part2_part3":[{item1},{item2},{item3}],
   *        "id1_part2_part4":[{item4}],
   *        ...
   * }
   * @param {Array} items Array
   * @param {Function} idBuilder id builder function
   */
  buildCombinedOneToManyMap(items, idBuilder) {
    const map = {};
    if (items) {
      items.forEach((item) => {
        let key = idBuilder(item);
        let array = map[key];
        if (!array) {
          array = [];
          map[key] = array;
        }
        array.push(item);
      });
    }
    return map;
  },
};
